import { KIND_OF_TESTS, buildPromptHintExample } from '@/lib/promptHints'

/**
 * PromptHints (#1462) — guidance shown under the Agent Prompt box when there are
 * no candidate tests, so a first-timer never faces a blank-but-mandatory prompt.
 *
 * Pure guidance: tapping a chip seeds/appends prompt text, "Use this" drops in a
 * worked example. No AI, nothing saved. The example tailors from the app's domain
 * label when there is one, generic otherwise. The component itself is
 * presentational — the dialog owns the textarea and decides how inserted text is
 * applied.
 *
 * There was also an "Areas to test" row, seeded from the scanner's discovered
 * pages. It was removed because the underlying extraction was a regex over every
 * anchor on the landing page, so on a content-heavy site the chips were link
 * prose ("Use the new simpler search", "the vision") rather than areas — noise
 * the operator had to read past. Restoring it needs real navigation detection
 * first, not a re-wiring of this prop.
 */
export interface PromptHintsProps {
  /** App domain label (scanner's `applicationDomain`), for the example. */
  domain?: string
  /** Append a starter / area fragment to the prompt. */
  onInsert: (text: string) => void
  /** Replace the prompt with the worked example. */
  onUseExample: (text: string) => void
  /** #1494 — suppress the single worked-example row when the richer
   * `PromptExamples` block is rendered alongside (it would duplicate the
   * example). Chips + the upload tip still show. Default false. */
  hideExample?: boolean
}

export function PromptHints({ domain, onInsert, onUseExample, hideExample = false }: PromptHintsProps) {
  const example = buildPromptHintExample(domain)

  return (
    <div className="rounded border border-border bg-muted/30 p-3 text-xs">
      <div className="font-medium mb-2">💡 Build your own — tap to add to your prompt.</div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-muted-foreground w-20 shrink-0">Kind of tests</span>
        {KIND_OF_TESTS.map(k => (
          <button
            key={k.label}
            type="button"
            onClick={() => onInsert(k.starter)}
            className="rounded-full border border-border bg-card px-2.5 py-0.5 font-medium hover:border-primary/40"
          >
            {k.label}
          </button>
        ))}
      </div>

      {!hideExample && (
        <div className="flex items-start gap-2 border-t border-dashed border-border pt-2 mt-1">
          <div className="flex-1 min-w-0">
            <span className="text-muted-foreground font-medium">Example:</span>{' '}
            <span className="italic">{example}</span>
          </div>
          <button
            type="button"
            onClick={() => onUseExample(example)}
            className="shrink-0 rounded border border-primary/30 bg-primary/10 px-2 py-1 font-semibold text-primary"
          >
            Use this
          </button>
        </div>
      )}

      <p className="text-[10px] text-muted-foreground mt-2">
        Tip: upload reference documents on the Application page to get proposed tests specific to your product.
      </p>
    </div>
  )
}
